"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import Fuse from "fuse.js";
import { useQuery } from "@tanstack/react-query";
import { RiSearchLine } from "react-icons/ri";

import { cn } from "~/utils/fontsConfig";
import { slugifyUser } from "~/utils/string";
import { useEventContextV2 } from "~/app/providers";

import { Checkbox } from "~/components/shadcn/Checkbox";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { Input } from "~/components/v4/shadcn/Input";
import { SpinnerV1 } from "~/components/themed/spinner";

import {
    getFollowersInvitesSuggestion,
    GetProximityAndFollowersInvites,
} from "~/app/events/[slug]/invites/actions";

import { useInvites } from "../contexts/InvitesContext";
import { InviteWithId } from "../../actions";
import { TbCalendarEvent } from "react-icons/tb";

export const InviteFollowers = ({
    initialData,
}: {
    initialData: GetProximityAndFollowersInvites;
}) => {
    const event = useEventContextV2();
    const hostId = event?.community?.id ?? event?.user?.id;
    const [searchTerm, setSearchTerm] = useState("");
    const { selectedFollowers, setSelectedFollowers, setTotalCount } =
        useInvites();

    const { data: followers, isLoading } = useQuery(
        ["followers-invites", hostId, event?.id],
        () =>
            getFollowersInvitesSuggestion({
                params: {
                    communitySlug: event?.community?.slug,
                    eventSlug: event?.slug,
                    eventId: event?.id,
                },
            }),
        {
            initialData: initialData?.followers,
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (followers) {
            setTotalCount((prev) => ({
                ...prev,
                followers: followers.length,
            }));
        }
    }, [followers]);

    const fuse = useMemo(
        () =>
            new Fuse(followers, {
                keys: ["name", "email"],
                threshold: 0.3,
                ignoreLocation: true,
                shouldSort: true,
            }),
        [followers],
    );

    const filteredFollowers = useMemo(() => {
        if (!searchTerm.trim()) return followers;
        return fuse.search(searchTerm).map((result) => result.item);
    }, [searchTerm, fuse, followers]);

    const toggleFollowerSelection = (follower: InviteWithId) => {
        setSelectedFollowers((prev) =>
            prev.some((invite) => invite.id === follower.id)
                ? prev.filter((invite) => invite.id !== follower.id)
                : [...prev, follower],
        );
    };

    const toggleAllFollowers = () => {
        if (selectedFollowers.length === followers.length) {
            setSelectedFollowers([]);
        } else {
            setSelectedFollowers(
                followers.map((follower) => ({
                    id: follower.id,
                    name: follower.name,
                    email: follower.email ?? "",
                    image: follower.image,
                })),
            );
        }
    };

    if (isLoading) return <SpinnerV1 center />;

    return (
        <div>
            <div className="space-y-4">
                <div className="flex flex-col gap-4">
                    <span className="font-[500] text-white">Followers</span>

                    <Input
                        placeholder="Search by name, location or email"
                        className="mb-2 mt-2 w-full rounded-[10px]"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        prefixComponent={<RiSearchLine className="h-4 w-4" />}
                    />

                    <div className="ml-2 mt-2 flex items-center gap-2">
                        <Checkbox
                            className={cn(
                                "rounded-[4px] border-2",
                                "data-[state=checked]:border-none data-[state=checked]:bg-v3-clover",
                                "data-[state=checked]:text-white",
                            )}
                            checked={
                                selectedFollowers.length === followers.length &&
                                followers.length > 0
                            }
                            onCheckedChange={toggleAllFollowers}
                        />
                        <span className="text-sm text-gray-400">
                            Select all {followers.length} followers
                        </span>
                    </div>
                </div>

                {filteredFollowers.length > 0 && (
                    <div className="max-h-[450px] space-y-2 overflow-y-scroll pb-20 md:pb-10">
                        {filteredFollowers.map((follower) => (
                            <div
                                key={follower.id}
                                className="flex items-center justify-between rounded-lg p-2"
                            >
                                <div className="flex items-center gap-4">
                                    <Checkbox
                                        className={cn(
                                            "rounded-[4px] border-2",
                                            "data-[state=checked]:border-none data-[state=checked]:bg-v3-clover",
                                            "data-[state=checked]:text-white",
                                        )}
                                        checked={selectedFollowers.some(
                                            (invite) =>
                                                invite.id === follower.id,
                                        )}
                                        onCheckedChange={() =>
                                            toggleFollowerSelection({
                                                id: follower.id,
                                                name: follower.name,
                                                email: follower.email ?? "",
                                                image: follower.image,
                                            })
                                        }
                                    />
                                    <Link
                                        href={`/profile/${slugifyUser(follower)}`}
                                        target="_blank"
                                    >
                                        <UserAvatar
                                            user={follower}
                                            className="h-10 w-10"
                                        />
                                    </Link>

                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm text-white">
                                            {follower.name}
                                        </span>
                                        <div className="flex items-center gap-1">
                                            <TbCalendarEvent className="h-4 w-4" />
                                            <span className="text-sm">
                                                Attended{" "}
                                                {follower.eventsAttended} events
                                                you hosted.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
